// @ts-nocheck
/**
 * Redirect-rule helpers.
 *
 * Rules come from Strapi ("redirect-rules"), shaped like:
 *   {
 *     redirectTo: "https://vxi.com/what-we-do",
 *     redirectFromList: [{ redirectFrom: "solutions" }, { redirectFrom: "industries" }]
 *   }
 *
 * `redirectFrom` values are authored by hand in the CMS, so they arrive in all
 * sorts of shapes: bare paths (no leading/trailing slash), mixed case, full
 * URLs, or paths with a query string. The router, on the other hand, hands us
 * `slug.join("/").toLowerCase()`. Every comparison therefore goes through
 * `normalizePath` so both sides always look the same.
 */

/** Normalise a redirect source, slug, or target into a comparable path key. */
export const normalizePath = (value) => {
  if (value == null) return "";

  let path = `${value}`.trim();
  if (!path) return "";

  try {
    path = decodeURIComponent(path);
  } catch {
    // Malformed URI -> compare the raw value instead of throwing.
  }

  // Full URLs -> compare on the pathname only.
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      path = path.replace(/^https?:\/\//i, "");
    }
  }

  return path
    .split("?")[0]
    .split("#")[0]
    .replace(/\/{2,}/g, "/")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "")
    .toLowerCase();
};

/** Strapi v4 nests fields under `attributes`; some responses are flat. */
const getRuleAttributes = (rule) => rule?.attributes ?? rule ?? null;

/** `redirectFromList` may be a repeatable component, a string, or absent. */
const getRedirectFromList = (rule) => {
  const attrs = getRuleAttributes(rule);
  if (!attrs) return [];

  const list = attrs.redirectFromList;

  if (Array.isArray(list)) {
    return list
      .map((entry) => (typeof entry === "string" ? entry : entry?.redirectFrom))
      .filter(Boolean);
  }

  if (typeof list === "string") {
    return list
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return [];
};

/**
 * Resolve the `redirectTo` target for `slugPath`, or `null` when no rule matches.
 * Self-referencing rules are ignored so a bad entry can never loop forever.
 */
export const findRedirectTarget = (redirectRules, slugPath) => {
  if (!Array.isArray(redirectRules) || redirectRules.length === 0) return null;

  const source = normalizePath(slugPath);
  if (!source) return null;

  for (const rule of redirectRules) {
    const target = getRuleAttributes(rule)?.redirectTo;
    if (!target) continue;

    const matches = getRedirectFromList(rule).some(
      (entry) => normalizePath(entry) === source
    );
    if (!matches) continue;

    // A rule that points at itself would reload the same URL forever.
    if (normalizePath(target) === source) return null;

    return target;
  }

  return null;
};

/**
 * Same-site absolute URLs are reduced to a path so Next can route them
 * internally (`https://vxi.com/what-we-do` -> `/what-we-do`). Everything else
 * (other hosts, relative paths) is returned untouched.
 */
export const toSameSitePath = (target) => {
  if (typeof target !== "string") return target;

  try {
    const url = new URL(target);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();

    if (host === "vxi.com") {
      return `${url.pathname}${url.search}${url.hash}` || "/";
    }
  } catch {
    // Not an absolute URL -> use as-is.
  }

  return target;
};
